// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

export const psiRootPath = "psi";
export const sarRootPath = "sar";

export const PSI_PATHS: Record<PSIPage, string> = {
  psi: `/${psiRootPath}`,
  dashboard: `/${psiRootPath}/dashboard`,
  staffDashboard: `/${psiRootPath}/dashboard/staff/:staffPseudoId`,
  caseDetails: `/${psiRootPath}/dashboard/staff/:staffPseudoId/case/:caseId`,
  supervisorDashboard: `/${psiRootPath}/dashboard/supervisor/:staffPseudoId`,
};

export const SAR_PATHS: Record<SARPage, string> = {
  sar: `/${sarRootPath}`,
  staffDashboard: `/${sarRootPath}/dashboard/staff/:staffPseudoId`,
  sarDetails: `/${sarRootPath}/dashboard/staff/:staffPseudoId/report/:sarId`,
};

export type PSIPage = keyof typeof PSI_PAGES;
export type SARPage = keyof typeof SAR_PAGES;

export const PSI_PAGES = {
  psi: psiRootPath,
  dashboard: "dashboard",
  staffDashboard: "staffDashboard",
  supervisorDashboard: "supervisorDashboard",
  caseDetails: "caseDetails",
} as const;

export const SAR_PAGES = {
  sar: sarRootPath,
  staffDashboard: "staffDashboard",
  sarDetails: "sarDetails",
} as const;

/** @returns the relative route template string for a PSI page */
export const psiRoute = ({ routeName }: { routeName: PSIPage }): string => {
  return PSI_PATHS[routeName].replace(PSI_PATHS.psi, "");
};

/** @returns the relative route template string for a SAR page */
export const sarRoute = ({ routeName }: { routeName: SARPage }): string => {
  return SAR_PATHS[routeName].replace(SAR_PATHS.sar, "");
};

export type PSIRouteParams = {
  staffPseudoId: string;
  caseId?: string;
};

export type SARRouteParams = {
  staffPseudoId: string;
  sarId?: string;
};

function buildUrl<T extends string>(
  paths: Record<T, string>,
  routeName: T,
  params?: Record<string, string>,
): string {
  if (params) {
    let path = paths[routeName];
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, value);
    });
    return path;
  }
  return paths[routeName];
}

export const psiUrl = (routeName: PSIPage, params: PSIRouteParams): string => {
  return buildUrl(PSI_PATHS, routeName, params);
};

export const sarUrl = (routeName: SARPage, params: SARRouteParams): string => {
  return buildUrl(SAR_PATHS, routeName, params);
};
